import Phaser from "phaser";
import type { RegionEncounterTimersSnapshot } from "@ninja/contracts";
import type { MapRuntimeDefinition } from "@ninja/game-content";
import { regionEncounterTimerLabels } from "../ui/regionEncounterTimerModel.js";
import {
  regionEncounterTimerAnchorsFor,
  type RegionEncounterTimerAnchor,
} from "./regionEncounterTimerMapModel.js";

const TIMER_MARKER_DEPTH = 10_010;
const TIMER_MARKER_COLOR = 0xf2b544;
const REFRESH_INTERVAL_MS = 1_000;
const TIMER_PLATE_MIN_WIDTH = 120;
const TIMER_PLATE_HORIZONTAL_PADDING = 12;

interface TimerMarkerView {
  anchor: RegionEncounterTimerAnchor;
  root: Phaser.GameObjects.Container;
  plate: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
  plateHeight: number;
}

export class RegionEncounterTimerMapRenderer {
  private readonly markers: TimerMarkerView[];
  private snapshot: RegionEncounterTimersSnapshot | undefined;
  private snapshotReceivedAt = 0;
  private refreshTimer: Phaser.Time.TimerEvent | undefined;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly map: MapRuntimeDefinition,
  ) {
    this.markers = regionEncounterTimerAnchorsFor(map).map((anchor) =>
      this.createMarker(anchor),
    );
  }

  update(snapshot: RegionEncounterTimersSnapshot): void {
    if (this.markers.length === 0) return;
    this.snapshot = snapshot;
    this.snapshotReceivedAt = performance.now();
    if (!this.refreshTimer)
      this.refreshTimer = this.scene.time.addEvent({
        delay: REFRESH_INTERVAL_MS,
        loop: true,
        callback: () => this.render(),
      });
    this.render();
  }

  destroy(): void {
    this.refreshTimer?.remove();
    this.refreshTimer = undefined;
    for (const marker of this.markers) marker.root.destroy(true);
    this.markers.length = 0;
    this.snapshot = undefined;
  }

  private createMarker(anchor: RegionEncounterTimerAnchor): TimerMarkerView {
    const isElite = anchor.kind === "elite";
    const timerX = isElite ? 0 : -10;
    const timerY = isElite ? -65 : -36;
    const labelX = isElite ? -28 : -38;
    const labelY = isElite ? -70 : -40;
    const plateHeight = isElite ? 18 : 22;
    const plate = this.scene.add
      .rectangle(
        timerX,
        timerY,
        TIMER_PLATE_MIN_WIDTH,
        plateHeight,
        0x07121c
      )
      .setStrokeStyle(1, TIMER_MARKER_COLOR, 0.95);
    const label = this.scene.add
      .text(labelX, labelY, "", {
        color: "#f2b544",
        fontFamily: "monospace",
        fontSize: isElite ? "9px" : "8px",
        fontStyle: "bold",
        align: "center",
      })
      .setOrigin(0.5)
      .setResolution(2);
    const root = this.scene.add
      .container(anchor.x, anchor.y, [plate, label])
      .setDepth(TIMER_MARKER_DEPTH)
      .setVisible(false);
    return { anchor, root, plate, label, plateHeight };
  }

  private render(): void {
    const snapshot = this.snapshot;
    if (!snapshot) return;
    const now =
      snapshot.serverTimeMs + (performance.now() - this.snapshotReceivedAt);
    const belongsToMap =
      snapshot.regionId === this.map.regionNavigation?.regionId;
    const labels = belongsToMap
      ? regionEncounterTimerLabels(snapshot, now)
      : { elite: null, boss: null };

    for (const marker of this.markers) {
      const value =
        marker.anchor.kind === "elite"
          ? marker.anchor.monsterId === snapshot.elite.monsterId
            ? labels.elite
            : null
          : marker.anchor.monsterId === snapshot.boss.monsterId
            ? labels.boss
            : null;
      marker.label.setText(value ?? "");
      marker.plate.setSize(
        Math.max(
          TIMER_PLATE_MIN_WIDTH,
          marker.label.width + TIMER_PLATE_HORIZONTAL_PADDING,
        ),
        marker.plateHeight,
      );
      marker.root.setVisible(value !== null);
    }
  }
}
